import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import PhoneInput, { parsePhoneNumber } from 'react-phone-number-input';
import { MdCheckCircle, MdLock, MdSms, MdTune } from 'react-icons/md';

import { PrimaryButton } from '..';
import { PhoneApi } from '../../util/api';
import { useIntl } from '../../util/reactIntl';
import {
  canUserSetPhoneNumber,
  getUserPhoneData,
  isPhoneVerifiedForUser,
  maskPhoneNumber,
  normalizePhoneNumber,
  PHONE_CHANGE_COOLDOWN_DAYS_VALUE,
} from '../../util/phoneNumber';
import '../../styles/PhoneNumberInputGlobal.css';

import css from './PhoneVerification.module.css';

const DEFAULT_PHONE_COUNTRY = 'US';
const DEFAULT_COUNTRY_CALLING_CODE = '1';

const getCountryCallingCode = phoneNumber => {
  try {
    const parsedPhoneNumber = parsePhoneNumber(phoneNumber);
    return parsedPhoneNumber?.countryCallingCode || DEFAULT_COUNTRY_CALLING_CODE;
  } catch (_error) {
    return DEFAULT_COUNTRY_CALLING_CODE;
  }
};

export const getPhoneVerificationPayload = phoneNumber => {
  const countryCode = getCountryCallingCode(phoneNumber);
  return {
    phoneNumber: normalizePhoneNumber(phoneNumber, countryCode) || phoneNumber,
    countryCode,
  };
};

const getErrorMessage = (error, intl) =>
  error?.message ||
  error?.statusText ||
  intl.formatMessage({ id: 'PhoneVerification.genericError' });

export const getPhoneChangeCooldownMessage = (intl, availableAt) => {
  const availableDate = Date.parse(availableAt || '');
  const availableDateText = Number.isFinite(availableDate)
    ? intl.formatDate(availableDate, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return intl.formatMessage(
    {
      id: availableDateText
        ? 'PhoneVerification.phoneChangeCooldownWithDate'
        : 'PhoneVerification.phoneChangeCooldown',
    },
    {
      days: PHONE_CHANGE_COOLDOWN_DAYS_VALUE,
      availableDate: availableDateText,
    }
  );
};

const PhoneVerification = props => {
  const intl = useIntl();
  const {
    className,
    currentUser,
    initialPhoneNumber,
    onPhoneNumberChange,
    onVerified,
    compact = false,
    showFrame = true,
    showTitle = true,
    showVerifiedSummary = true,
    title,
  } = props;
  const titleText =
    typeof title === 'undefined'
      ? intl.formatMessage({ id: 'PhoneVerification.defaultTitle' })
      : title;

  const profilePhoneData = useMemo(() => getUserPhoneData(currentUser), [currentUser]);
  const initialPhone = initialPhoneNumber || profilePhoneData.phoneNumber || '';
  const initiallyVerified = isPhoneVerifiedForUser(currentUser, initialPhone);
  const [phoneNumber, setPhoneNumber] = useState(initialPhone);
  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState(
    initiallyVerified ? normalizePhoneNumber(initialPhone) : ''
  );
  const [consentAccepted, setConsentAccepted] = useState(initiallyVerified);
  const [mode, setMode] = useState(initiallyVerified ? 'verified' : 'initial');
  const [code, setCode] = useState('');
  const [inProgress, setInProgress] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const nextInitialPhone = initialPhoneNumber || profilePhoneData.phoneNumber || '';
    const nextVerified = isPhoneVerifiedForUser(currentUser, nextInitialPhone);
    setPhoneNumber(nextInitialPhone);
    setVerifiedPhoneNumber(nextVerified ? normalizePhoneNumber(nextInitialPhone) : '');
    setConsentAccepted(nextVerified);
    setMode(nextVerified ? 'verified' : 'initial');
  }, [currentUser, initialPhoneNumber, profilePhoneData.phoneNumber]);

  const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
  const isVerified = Boolean(
    verifiedPhoneNumber &&
      normalizedPhoneNumber &&
      normalizePhoneNumber(verifiedPhoneNumber) === normalizedPhoneNumber
  );
  const maskedPhone = isVerified
    ? maskPhoneNumber(verifiedPhoneNumber)
    : maskPhoneNumber(phoneNumber || profilePhoneData.phoneNumber);
  const phoneChangeCheck = useMemo(
    () => canUserSetPhoneNumber({ user: currentUser, phoneNumber }),
    [currentUser, phoneNumber]
  );
  const phoneChangeGuardMessage = phoneChangeCheck.allowed
    ? null
    : getPhoneChangeCooldownMessage(intl, phoneChangeCheck.availableAt);
  const visibleError = phoneChangeGuardMessage || error;

  const handlePhoneChange = value => {
    const nextValue = value || '';
    setPhoneNumber(nextValue);
    setError(null);
    if (normalizePhoneNumber(verifiedPhoneNumber) !== normalizePhoneNumber(nextValue)) {
      setVerifiedPhoneNumber('');
      setMode('initial');
    }
    if (onPhoneNumberChange) {
      onPhoneNumberChange(nextValue);
    }
  };

  const sendCode = async () => {
    if (!consentAccepted) {
      setError(intl.formatMessage({ id: 'PhoneVerification.consentError' }));
      return;
    }

    if (phoneChangeGuardMessage) {
      setError(phoneChangeGuardMessage);
      return;
    }

    setInProgress(true);
    setError(null);
    try {
      await PhoneApi.sendVerificationCode(getPhoneVerificationPayload(phoneNumber));
      setMode('otp');
    } catch (e) {
      setError(getErrorMessage(e, intl));
    } finally {
      setInProgress(false);
    }
  };

  const verifyCode = async () => {
    setInProgress(true);
    setError(null);
    try {
      const response = await PhoneApi.verifyCode({
        ...getPhoneVerificationPayload(phoneNumber),
        code,
      });
      const verifiedNumber = response?.data?.phoneNumber || normalizePhoneNumber(phoneNumber);
      if (onVerified) {
        await onVerified(response?.data || { phoneNumber: verifiedNumber });
      }
      setVerifiedPhoneNumber(verifiedNumber);
      setPhoneNumber(verifiedNumber);
      setMode('verified');
      setCode('');
      if (onPhoneNumberChange) {
        onPhoneNumberChange(verifiedNumber);
      }
    } catch (e) {
      setError(getErrorMessage(e, intl));
    } finally {
      setInProgress(false);
    }
  };

  const handleChangeNumber = () => {
    setVerifiedPhoneNumber('');
    setMode('initial');
    setError(null);
  };

  if (isVerified && mode === 'verified') {
    if (!showVerifiedSummary) {
      return (
        <div
          className={classNames(
            css.root,
            {
              [css.plainRoot]: !showFrame,
              [css.compactRoot]: compact,
            },
            className
          )}
        >
          <div className={css.verifyCard}>
            {showTitle ? <h3 className={css.verifyTitle}>{titleText}</h3> : null}
            <div className={css.verifiedInline}>
              <span className={css.maskedPhone}>{maskedPhone}</span>
              <span className={css.verifiedBadge}>
                <MdCheckCircle aria-hidden="true" />
                <span>{intl.formatMessage({ id: 'PhoneVerification.verified' })}</span>
              </span>
              <button type="button" className={css.linkButton} onClick={handleChangeNumber}>
                {intl.formatMessage({ id: 'PhoneVerification.changeNumber' })}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={classNames(css.root, className)}>
        <div className={css.card}>
          <div className={css.cardHeader}>
            <h3 className={css.title}>
              {intl.formatMessage({ id: 'PhoneVerification.textMessageNotifications' })}
            </h3>
          </div>
          <div className={css.verifiedSummary}>
            <span className={css.maskedPhone}>{maskedPhone}</span>
            <span className={css.verifiedBadge}>
              <MdCheckCircle aria-hidden="true" />
              <span>{intl.formatMessage({ id: 'PhoneVerification.verified' })}</span>
            </span>
            <button type="button" className={css.linkButton} onClick={handleChangeNumber}>
              {intl.formatMessage({ id: 'PhoneVerification.changeNumber' })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        css.root,
        {
          [css.plainRoot]: !showFrame,
          [css.compactRoot]: compact,
        },
        className
      )}
    >
      <div className={css.verifyCard}>
        {showTitle ? <h3 className={css.verifyTitle}>{titleText}</h3> : null}
        <p className={css.helpText}>{intl.formatMessage({ id: 'PhoneVerification.helpText' })}</p>

        {mode === 'otp' ? (
          <>
            <input
              className={css.otpInput}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder={intl.formatMessage({ id: 'PhoneVerification.otpPlaceholder' })}
            />
            <p className={css.smallText}>
              {intl.formatMessage(
                { id: 'PhoneVerification.codeSent' },
                {
                  phoneNumber:
                    maskedPhone || intl.formatMessage({ id: 'PhoneVerification.phoneFallback' }),
                }
              )}{' '}
              <button className={css.textButton} type="button" onClick={sendCode}>
                {intl.formatMessage({ id: 'PhoneVerification.resend' })}
              </button>
            </p>
            {visibleError ? <p className={css.error}>{visibleError}</p> : null}
            <PrimaryButton
              type="button"
              className={css.primaryButton}
              inProgress={inProgress}
              disabled={!code || inProgress}
              onClick={verifyCode}
            >
              {intl.formatMessage({ id: 'PhoneVerification.verifyPhoneNumber' })}
            </PrimaryButton>
          </>
        ) : (
          <>
            <PhoneInput
              className={classNames(css.phoneInput, { [css.phoneInputError]: visibleError })}
              value={phoneNumber}
              defaultCountry={DEFAULT_PHONE_COUNTRY}
              international
              countryCallingCodeEditable={false}
              placeholder={intl.formatMessage({ id: 'PhoneVerification.phonePlaceholder' })}
              onChange={handlePhoneChange}
            />
            <label className={css.consentRow}>
              <input
                type="checkbox"
                checked={consentAccepted}
                onChange={e => setConsentAccepted(e.target.checked)}
              />
              <span>
                {intl.formatMessage(
                  { id: 'PhoneVerification.consentCopy' },
                  {
                    termsOfService: chunks => (
                      <a href="/p/terms-of-service" target="_blank" rel="noreferrer">
                        {chunks}
                      </a>
                    ),
                    privacyPolicy: chunks => (
                      <a href="/p/privacy-policy" target="_blank" rel="noreferrer">
                        {chunks}
                      </a>
                    ),
                  }
                )}
              </span>
            </label>
            {visibleError ? <p className={css.error}>{visibleError}</p> : null}
            <PrimaryButton
              type="button"
              className={css.primaryButton}
              inProgress={inProgress}
              disabled={
                !phoneNumber || !consentAccepted || inProgress || Boolean(phoneChangeGuardMessage)
              }
              onClick={sendCode}
            >
              {intl.formatMessage({ id: 'PhoneVerification.sendVerificationCode' })}
            </PrimaryButton>
          </>
        )}

        <ul className={css.securityList}>
          <li>
            <span className={css.securityIcon}>
              <MdLock aria-hidden="true" />
            </span>
            <span>{intl.formatMessage({ id: 'PhoneVerification.storedSecurely' })}</span>
          </li>
          <li>
            <span className={css.securityIcon}>
              <MdSms aria-hidden="true" />
            </span>
            <span>{intl.formatMessage({ id: 'PhoneVerification.usedForUpdates' })}</span>
          </li>
          <li>
            <span className={css.securityIcon}>
              <MdTune aria-hidden="true" />
            </span>
            <span>{intl.formatMessage({ id: 'PhoneVerification.optOutAnytime' })}</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PhoneVerification;
